import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  Building2Icon,
  Button,
  EmptyState,
  Flex,
  NewBadge,
  Tabs,
  Text,
  toast,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationSwitcher } from '~publish/components/MainSidebar/components/OrganizationSwitcher/useOrganizationSwitcher'
import {
  useAccount,
  useIsOwner,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import { APIClientSection } from './components/APIClientSection'
import { ApiUpgradeDialog } from './components/ApiUpgradeDialog'
import { ConnectionsSection } from './components/ConnectionsSection'
import { DeleteClientDialog } from './components/DeleteClientDialog'
import { PageActions } from './components/PageActions'
import { PersonalTokenSection } from './components/PersonalTokenSection'
import { UsagePanel } from './components/UsagePanel'
import {
  useAPIClients,
  useDeleteAPIClient,
  useGeneratePersonalAccessToken,
  usePersonalAccessTokens,
  useRevokePersonalAccessToken,
} from './hooks'
import type { APIClient } from './hooks'
import { EmailVerificationGate } from '../shared/components/EmailVerificationGate'
import { ExpiredTokenIndicator } from '../shared/components/ExpiredTokenIndicator'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'
import { SettingsPageSkeleton } from '../shared/components/SettingsPageSkeleton'
import { useConnectedApps } from '../shared/hooks/useConnectedApps'

import styles from './DeveloperDashboard.module.css'

const ONBOARDING_BANNER_ID = 'api-developer-onboarding'
const API_DOCS_COACHMARK_ID = 'api-docs-coachmark'

const TAB_VALUES = ['personal-keys', 'app-clients'] as const
type TabValue = (typeof TAB_VALUES)[number]

const isTabValue = (value: string | null): value is TabValue =>
  value !== null && TAB_VALUES.some((tab) => tab === value)

const PageHeader = (): JSX.Element => (
  <Flex align="center" gap="xs">
    Buffer API
    <NewBadge />
  </Flex>
)

const VerifiedDeveloperDashboard = (): JSX.Element => {
  const organizationId = useOrganizationId()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    tokens,
    maxTokens,
    canUpgradeForMore: canUpgradeForMoreTokens,
    isLoading: isPersonalAccessTokenLoading,
    hasError,
    hasExpiredToken,
    refetch,
  } = usePersonalAccessTokens()
  const {
    clients,
    maxClients,
    canUpgradeForMore: canUpgradeForMoreClients,
    isLoading: isClientLoading,
  } = useAPIClients()
  const { generateToken, isLoading: isGenerating } =
    useGeneratePersonalAccessToken()
  const { revokeToken, isLoading: isRevoking } = useRevokePersonalAccessToken()
  const { deleteClient, isLoading: isDeleting } = useDeleteAPIClient()
  const { connectedApps } = useConnectedApps()
  const [clientToDelete, setClientToDelete] = useState<APIClient | null>(null)

  // MCP calls are metered on the personal client too, so a user whose only
  // API traffic is an integration still has a bucket worth showing.
  const hasMcpConnections = connectedApps.some((app) => app.category === 'mcp')

  const personalClient = clients?.find((el) =>
    el.clientName?.includes('personal-'),
  )
  const personalClientId = personalClient?.clientId ?? null
  const apiClients =
    clients?.filter((el) => !el.clientName?.includes('personal-')) ?? []

  const [upgradeReason, setUpgradeReason] = useState<
    'key-limit' | 'rate-limit' | null
  >(null)

  const onboardingBanner = useDismissableBanner(ONBOARDING_BANNER_ID)

  const handleTokenListRefresh = async (): Promise<void> => {
    try {
      await refetch()
    } catch {
      toast.error('Unable to refresh key list. Please refresh the page.')
    }
  }

  const navigateToClient = (client: APIClient | null): void => {
    if (client) {
      navigate(`/settings/api/client/${client.clientId}`)
    } else {
      navigate('/settings/api/client/new')
    }
  }

  const requestedTab = searchParams.get('tab')
  const activeTab: TabValue = isTabValue(requestedTab)
    ? requestedTab
    : 'personal-keys'

  const handleTabChange = (next: string): void => {
    if (!isTabValue(next)) {
      return
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', next)
    setSearchParams(nextParams, { replace: true })
  }

  if (isClientLoading || isPersonalAccessTokenLoading) {
    return <SettingsPageSkeleton />
  }

  if (hasError) {
    return (
      <EmptyState
        variant="critical"
        size="xlarge"
        className={styles.emptyState}
      >
        <EmptyState.Heading>Unable to load tokens</EmptyState.Heading>
        <EmptyState.Description>
          Something went wrong while loading your API tokens. Please try again
          later.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <Tabs value={activeTab} onChange={handleTabChange}>
      <Tabs.TabList>
        <Tabs.Tab value="personal-keys">
          Personal Access
          {hasExpiredToken && <ExpiredTokenIndicator />}
        </Tabs.Tab>

        <Tabs.Tab value="app-clients">App Clients</Tabs.Tab>
      </Tabs.TabList>

      <Tabs.Panel value="personal-keys" className={styles.panel}>
        <Flex direction="column" gap="lg" className={styles.tabPanel}>
          {tokens.length > 0 && (
            <Text color="subtle">
              Access your own Buffer data to build personal automations and
              integrations.
            </Text>
          )}

          {(tokens.length > 0 || hasMcpConnections) && (
            <UsagePanel
              clientId={personalClientId}
              footnote="Keys and Active Integrations share this limit."
              canUpgradeForMore={canUpgradeForMoreTokens}
              onUpgrade={(): void => setUpgradeReason('rate-limit')}
            />
          )}

          <PersonalTokenSection
            tokens={tokens}
            maxTokens={maxTokens}
            canUpgradeForMore={canUpgradeForMoreTokens}
            organizationId={organizationId}
            generateToken={generateToken}
            revokeToken={revokeToken}
            isGenerating={isGenerating}
            isRevoking={isRevoking}
            onTokenListRefresh={handleTokenListRefresh}
            onUpgrade={(): void => setUpgradeReason('key-limit')}
            showOnboarding={onboardingBanner.isActive}
            onDismissOnboarding={(): void => onboardingBanner.dismiss()}
          />

          <ConnectionsSection />
        </Flex>
      </Tabs.Panel>

      <Tabs.Panel value="app-clients" className={styles.panel}>
        <Flex direction="column" gap="xl" className={styles.tabPanel}>
          {apiClients.length > 0 && (
            <Text color="subtle">
              Let your users connect their Buffer accounts to your app.
            </Text>
          )}

          <APIClientSection
            apiClients={apiClients}
            maxClients={maxClients}
            canUpgradeForMore={canUpgradeForMoreClients}
            onCreateClient={(): void => {
              navigateToClient(null)
            }}
            onEditClient={(client: APIClient): void => {
              navigateToClient(client)
            }}
            onUpgrade={(): void => setUpgradeReason('key-limit')}
            onDeleteClient={(client: APIClient): void => {
              setClientToDelete(client)
            }}
          />
        </Flex>
      </Tabs.Panel>

      <ApiUpgradeDialog
        open={upgradeReason !== null}
        onOpenChange={(open): void => {
          if (!open) setUpgradeReason(null)
        }}
        upgradePathName={upgradeReason ?? 'key-limit'}
      />

      <DeleteClientDialog
        open={clientToDelete !== null}
        onOpenChange={(open): void => {
          if (!open) setClientToDelete(null)
        }}
        isLoading={isDeleting}
        onConfirm={async (): Promise<void> => {
          if (!clientToDelete) return
          const result = await deleteClient(clientToDelete.clientId)
          if (result.success) {
            setClientToDelete(null)
            toast.success('App client deleted successfully')
          } else {
            toast.error(result.error)
          }
        }}
      />
    </Tabs>
  )
}

const NonOwnerEmptyState = (): JSX.Element => {
  const { account } = useAccount()
  const { organizations, switchOrganization } = useOrganizationSwitcher()

  const ownedOrganization = organizations.find(
    (org) => org.ownerEmail === account?.email,
  )

  const handleSwitch = async (): Promise<void> => {
    if (ownedOrganization) {
      await switchOrganization(ownedOrganization.id)
    }
  }

  return (
    <EmptyState size="xlarge" className={styles.emptyState}>
      <EmptyState.Icon>
        <Building2Icon />
      </EmptyState.Icon>
      <EmptyState.Heading>
        Switch to your organization to use the API
      </EmptyState.Heading>
      <EmptyState.Description>
        To access this organization via API, switch to an organization you own
        to get a key. You&apos;ll have access based on the permissions granted
        to you.
      </EmptyState.Description>
      {ownedOrganization && (
        <EmptyState.Actions>
          <Button variant="primary" onClick={handleSwitch}>
            Switch Organization
          </Button>
        </EmptyState.Actions>
      )}
    </EmptyState>
  )
}

const DeveloperDashboard = (): JSX.Element => {
  const { loading: isAccountLoading } = useAccount()
  const organizationId = useOrganizationId()
  const isOwner = useIsOwner()
  const onboardingBanner = useDismissableBanner(ONBOARDING_BANNER_ID)
  const apiDocsCoachMark = useDismissableBanner(API_DOCS_COACHMARK_ID)

  const showApiDocsCoachMark =
    !onboardingBanner.isActive && apiDocsCoachMark.isActive

  const apiSettingsViewedFiredRef = useRef(false)
  useEffect(() => {
    if (apiSettingsViewedFiredRef.current) return
    if (!organizationId) return
    // Function name is "aPISettingsViewed" because Typewriter lowercases only the
    // first letter of event names beginning with an acronym (e.g. cTAClicked).
    BufferTracker.aPISettingsViewed({
      organizationId,
      clientName: Client.PublishWeb,
      cta: null,
    })
    apiSettingsViewedFiredRef.current = true
  }, [organizationId])

  return (
    <NewSettingsPageLayout
      title={<PageHeader />}
      pageTitle="Buffer API"
      subtitle="Build apps, integrations, and automations."
      actions={
        <PageActions
          showCoachMark={showApiDocsCoachMark}
          onCoachMarkDismiss={(): void => apiDocsCoachMark.dismiss()}
        />
      }
      gap="xl"
    >
      {isAccountLoading ? (
        <SettingsPageSkeleton />
      ) : isOwner ? (
        <EmailVerificationGate>
          <VerifiedDeveloperDashboard />
        </EmailVerificationGate>
      ) : (
        <NonOwnerEmptyState />
      )}
    </NewSettingsPageLayout>
  )
}

export default DeveloperDashboard
